import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useEffect, useRef, useState } from "react";

//Components
import { Modal, ModalBody, ModalFooter, Button, Form, Input, ToggleSwitch } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';

//Actions
import {
    smsSystemEditRequest,
    smsSystemCreateRequest,
    smsSystemGetRequest,
    clearRedux,
} from 'Redux/actions';

const CreateEditSimProvider = (props) => {
    const { tableRef } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const formRef = useRef();

    //Local States
    const [name, setName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [priority, setPriority] = useState(null);
    const [active, setActive] = useState(true);
    const [isSMPP, setIsSMPP] = useState(false);

    //Redux States
    const {
        openModal,
        loading,
        create,
        update,
        smsSystem,
        current,
    } = useSelector(state => state.createEditSmsSystemRedux);

    const closeModal = () => {
        setName('');
        setDisplayName('');
        setPriority(null);
        setActive(true);
        setIsSMPP(false);
        dispatch(clearRedux("SMS_SYSTEM_CREATE_EDIT"))
    }

    const applyChanges = () => {
        let payload = {
            name,
            displayName,
            priority,
            isSMPP,
            active,
        }
        if (current?.id) {
            payload.id = current?.id
            dispatch(smsSystemEditRequest(payload))
        } else dispatch(smsSystemCreateRequest(payload))
    }

    useEffect(() => {
        if (create?.id) {
            NotificationManager.success(
                messages['sms_system_create_success_message'],
                messages['sms_system_create_title'],
                3000
            );
            closeModal();
            tableRef && tableRef?.current?.getData();
        } else if (create?.error) {
            let message = create?.error

            if (message.includes("name exists already")) {
                message = messages['sms_system_same_name_exits']
            }

            NotificationManager.error(
                message,
                messages['sms_system_create_title'],
                3000
            );
            dispatch(clearRedux("SMS_SYSTEM_CREATE_EDIT", { create: null }))
        }

        if (update?.id) {
            NotificationManager.success(
                messages['sms_system_edit_success_message'],
                messages['sms_system_edit_title'],
                3000
            );

            closeModal();
            tableRef && tableRef?.current?.getData();
            dispatch(clearRedux("SMS_SYSTEM_CREATE_EDIT", { update: null }))
        } else if (update?.error) {
            let message = update?.error

            if (message.includes("already exists")) {
                message = messages['sms_system_same_name_exits_edit']
            }

            NotificationManager.error(
                message,
                messages['sms_system_edit_title'],
                3000
            );
            dispatch(clearRedux("SMS_SYSTEM_CREATE_EDIT", { update: null }))
        }

        if (smsSystem) {
            setName(smsSystem?.name);
            setDisplayName(smsSystem?.displayName);
            setPriority(smsSystem?.priority);
            setIsSMPP(smsSystem?.isSMPP);
            setActive(smsSystem?.active);
            dispatch(clearRedux("SMS_SYSTEM_CREATE_EDIT", { smsSystem: null }))
        }

    }, [create, update, smsSystem])

    useEffect(() => {
        if (openModal && current?.id) {
            dispatch(smsSystemGetRequest(current?.id))
        }
    }, [openModal])

    return (
        <Modal
            open={openModal}
            title={current?.id ? `${messages['sms_system_edit_title']} (${current?.name})` : messages['sms_system_create_title']}
            handleClose={() => closeModal()}
            position="right"
            loading={loading}
            size="small"
        >
            <ModalBody>
                <div style={{ padding: '0 10px', height: 600 }}>
                    <Form
                        ref={formRef}
                        onValidSubmit={() => applyChanges()}
                    >
                        <div style={{ marginTop: 5 }}>
                            <Input
                                label={messages['sms_system_name']}
                                placeholder={messages['sms_system_name']}
                                value={name}
                                onChange={setName}
                                maxLength={25}
                                required
                                requiredMessage={messages['required_field']}
                                formatMessage={messages['common_error_format']}
                            />
                        </div>
                        <div style={{ marginTop: 5 }}>
                            <Input
                                label={messages['sms_system_display_name']}
                                placeholder={messages['sms_system_display_name']}
                                value={displayName}
                                onChange={setDisplayName}
                                maxLength={50}
                                required
                                requiredMessage={messages['required_field']}
                                formatMessage={messages['common_error_format']}
                            />
                        </div>
                        {
                            current?.id && (
                                <div style={{ marginTop: 5 }}>
                                    <Input
                                        label={messages['sms_system_priority']}
                                        placeholder={messages['sms_system_priority']}
                                        value={priority}
                                        min={1}
                                        minMessage={`${messages["create_device_model_min_number"]} 1`}
                                        onChange={(value) => {
                                            if (value?.length <= 2) setPriority(value)
                                        }}
                                        maxLength={2}
                                        required
                                        requiredMessage={messages['required_field']}
                                        type="number"
                                        formatMessage={messages['common_error_format']}
                                    />
                                </div>
                            )
                        }

                        <div style={{ textAlign: 'left', display: '', padding: '0 10px 0 0', width: "30%", marginTop: 5 }}>
                            <label className="input-normal-label">{messages['sms_system_is_smpp']}</label>
                            <ToggleSwitch
                                id="sms-system-isSMPP"
                                medium
                                blueToggle
                                optionLabels={[messages['create_account_yes'], messages['create_account_not']]}
                                checked={isSMPP}
                                onChange={(checked) => setIsSMPP(checked)}
                            />
                        </div>
                        <div style={{ textAlign: 'left', display: '', padding: '0 10px 0 0', width: "30%", marginTop: 5 }}>
                            <label className="input-normal-label">{messages['sms_system_status']}</label>
                            <ToggleSwitch
                                id="sms-system-status"
                                medium
                                blueToggle
                                optionLabels={[messages['common_active'], messages['common_inactive']]}
                                checked={active}
                                onChange={(checked) => setActive(checked)}
                            />
                        </div>
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn btn-cancel"
                    onClick={() => closeModal()}
                >
                    {messages['common_cancel']}
                </Button>
                <Button
                    className="btn-dark-blue"
                    onClick={() => {
                        formRef?.current?.submit()
                    }}
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default CreateEditSimProvider;